'use client';

import Card from '@tempcover/bolt-ui/card';
import { useState } from 'react';
import { IconPlus } from '@tabler/icons-react';

type Item = {
  title: string;
  content?: string;
  parentTitle?: string;
  parentUrl?: string;
};

interface AccordionProps {
  heading?: string;
  showIndex?: boolean;
  narrow?: boolean;
  isFaqs?: boolean;
  items: Item[];
}

export const Accordion = (
  { heading, items, showIndex, narrow, isFaqs = false }: AccordionProps = {
    showIndex: false,
    narrow: false,
    items: [],
  }
) => {
  const initialExpandedState = Array(items.length).fill(false);
  const [expandedState, setExpandedState] = useState(initialExpandedState);

  const toggleItemExpanded = (index: number) => {
    setExpandedState((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const Schema = () => {
    if (!isFaqs) return null;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.slice(0, 2).map((item: Item) => ({
        '@type': 'Question',
        name: item.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.content,
        },
      })),
    };

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
  };

  return (
    <>
      <Schema />

      {heading && <h2 className={`${narrow ? 'md:text-center' : ''} max-w-none`}>{heading}</h2>}

      <div className={`flex flex-col gap-3 py-5 ${narrow ? 'mx-auto max-w-md' : ''}`}>
        {items.map((item, index) => {
          const active = expandedState[index];
          const hasContent = !!item.content;
          const hasParent = !!item.parentTitle && !!item.parentUrl;

          const renderHeader = () => (
            <h3 className="m-0">
              <span
                className={`font-base flex w-full items-center justify-between px-2 text-left text-[16px] leading-[26px] font-bold md:text-[18px] ${hasContent ? 'hover:border-dark hover:text-dark transition-all' : ''}`}
              >
                {showIndex ? (
                  <div className="flex items-center gap-4">
                    <span className="tc-heading-xl m-0 inline-block text-inherit">
                      {index + 1 < 10 ? '0' : ''}
                      {index + 1}
                    </span>
                    <span>{item.title}</span>
                  </div>
                ) : (
                  <span>{item.title}</span>
                )}

                {hasContent && (
                  <IconPlus
                    aria-hidden="true"
                    size={24}
                    stroke={3}
                    strokeLinecap="square"
                    className={`transition-rotate ml-4 duration-600 ease-in-out ${
                      active ? 'rotate-45 stroke-(--color-dark)' : 'stroke-(--color-primary)'
                    }`}
                  />
                )}
              </span>
            </h3>
          );

          return (
            <Card
              key={`item-${index}`}
              {...(hasContent
                ? {
                    className: 'hover:shadow-primary/10 hover:border-primary transition-all cursor-pointer',
                    onClick: () => toggleItemExpanded(index),
                  }
                : {})}
            >
              {hasContent ? (
                <button
                  type="button"
                  aria-expanded={active}
                  aria-controls={`ac-region-${index}`}
                  id={`ac-button-${index}`}
                  className="w-full"
                >
                  {renderHeader()}
                </button>
              ) : (
                <div className="w-full">{renderHeader()}</div>
              )}

              {hasContent && (
                <>
                  <div
                    role="region"
                    aria-labelledby={`ac-button-${index}`}
                    id={`ac-region-${index}`}
                    className={`overflow-hidden px-2 text-left transition-[max-height] duration-600 ease-in-out ${
                      active ? 'max-h-1000' : 'max-h-0'
                    }`}
                    dangerouslySetInnerHTML={{ __html: `<br>${item.content}` }}
                  />
                  <em className="block px-2 text-sm underline">
                    {hasParent && <a href={item.parentUrl}>{item.parentTitle}</a>}
                  </em>
                </>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
};
